<script setup lang="ts">
  import { useSlots, computed, Comment, Text, Fragment, type VNode } from 'vue';
  import { mpfCardDefaults, MpfCardProps } from './MpfCard.props';
  import MpfTag from '../MpfTag/MpfTag.vue';

  const props = withDefaults(defineProps<MpfCardProps>(), mpfCardDefaults);
  defineOptions({ inheritAttrs: false });
  const emit = defineEmits<{
    (e: 'click', ev: MouseEvent): void;
  }>();

  const headingTag = computed(() => `h${props.headingLevel}` as keyof HTMLElementTagNameMap);

  const isClickable = computed(() => {
    if (props.disabled) return false;
    return !!props.href || !!props.clickable;
  });

  const cardClasses = computed(() => ({
    'mpf-card--primary': !!props.primary,
    'mpf-card--clickable': isClickable.value,
    'mpf-card--disabled': props.disabled,
  }));

  const tagColour = computed(() => (props.disabled ? 'grey' : 'blue'));

  const onClick = (event: MouseEvent) => {
    event.preventDefault();
    if (!isClickable.value) return;
    emit('click', event);
  };

  function hasMeaningfulContent(nodes: VNode[] | undefined): boolean {
    if (!nodes || nodes.length === 0) return false;

    return nodes.some((n) => {
      // Ignore comment placeholders like <!--v-if-->
      if (n.type === Comment) return false;

      // Ignore whitespace-only text nodes (indentation/newlines)
      if (n.type === Text) {
        const text = String(n.children ?? '');
        return text.trim().length > 0;
      }

      // Unwrap fragments (common with <template> or slot compilation)
      if (n.type === Fragment) {
        return hasMeaningfulContent(n.children as VNode[] | undefined);
      }

      // Anything else counts as real content
      return true;
    });
  }
  const slots = useSlots();
  const hasDefaultSlot = computed(() => {
    const content = slots.default?.();
    return hasMeaningfulContent(content as VNode[] | undefined);
  });
</script>

<template>
  <article class="mpf-card" :class="cardClasses">
    <div class="mpf-card__content mpf-grid-row">
      <div class="mpf-card__text-content mpf-grid-column-two-thirds">
        <div class="mpf-card__header">
          <component :is="headingTag" class="mpf-card__heading" :class="headingClasses">
            <a v-if="href" class="mpf-card__link" :href="href" @click="onClick">
              <slot name="heading">
                {{ heading }}
              </slot>
            </a>

            <template v-else>
              <slot name="heading">
                {{ heading }}
              </slot>
            </template>
          </component>
          <p v-if="description || $slots.description" class="mpf-card__description">
            <slot name="description">
              {{ description }}
            </slot>
          </p>
        </div>
      </div>
      <div v-if="tagText" class="mpf-card__tags mpf-grid-column-one-third">
        <MpfTag :colour="tagColour" :text="tagText" />
      </div>
      <div v-if="hasDefaultSlot" class="mpf-card__slot mpf-grid-column-full">
        <slot />
      </div>
    </div>
  </article>
</template>

<style scoped lang="scss">
  @use './MpfCard.scss';
</style>
